import { useEffect, useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { API, useListener } from '@unboared/react';
import { Highscore } from '@unboared/lib';
import { Database } from '~/services/db';
import { useAuth } from '~/services/auth';
import { useSyncUser } from '~/hooks/models/useUser';
import { handleAiBridgeMessage } from '~/services/quizStudio/aiBridge';

const TRANSMIT_CODE = 'unboared::webview::transmit';

/**
 * Bridge between the platform and the WebView game.
 * @param gameName le nom du jeu sous jacent
 * @param src l'url vers le jeu
 * @param clientID the client ID to pass to the game
 * @returns la référence vers le webview, s'il le webview (donc le jeu) est chargé
 */
export const useWebViewPlugin = (webviewRef: any, src: string, clientID?: string) => {
    // Instanciate a reference to the webview
    const [webviewLoaded, setWebviewLoaded] = useState(false);
    const language = 'en';

    // Get current user info
    const { auth } = useAuth();
    const user = useSyncUser(auth?.uid || 'unknown');
    const me = {
        uid: API.getUID(),
        username: API.getUsername(),
        avatar: API.getAvatar(),
        color: API.getColor(),
    };
    const type = API.isScreen() ? 'screen' : 'gamepad';

    // Envoie un message au Webview sous-jacent
    // -- ex : { }
    const postToWebView = useCallback(
        (message: any) => {
            if (!webviewRef.current) return;
            // console.log(message);
            // console.log(
            //   'Game Runner Screen Received Delay, ',
            //   Date.now() - message.data?.data?.timestamp
            // );
            webviewRef.current.contentWindow.postMessage(message, src);
        },
        [webviewRef]
    );

    /* Listen from onConnect method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onConnect((deviceID: string) => {
            postToWebView({
                action: 'connect',
                data: {
                    deviceID,
                    data: {
                        state: API.getDeviceState(deviceID),
                        player: API.getPlayer(deviceID),
                    },
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onDisonnect method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDisconnect((deviceID: string) => {
            postToWebView({
                action: 'disconnect',
                data: { deviceID },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPlayerChange((deviceID: string) => {
            postToWebView({
                action: 'setPlayer',
                data: {
                    deviceID,
                    player: API.getPlayer(deviceID),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDeviceStateChange((deviceID: string) => {
            postToWebView({
                action: 'setDeviceState',
                data: {
                    deviceID,
                    state: API.getDeviceState(deviceID),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDeviceStatePropertyChange((deviceID: string, key: string) => {
            postToWebView({
                action: 'setDeviceStatePropertyChange',
                data: {
                    deviceID,
                    key,
                    value: API.getDeviceStateProperty(deviceID, key),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onMute method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onMute((value: boolean) => {
            postToWebView({
                action: 'mute',
                data: { value },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onResume method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onResume(() => {
            postToWebView({ action: 'resume' });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPause method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPause(() => {
            postToWebView({ action: 'pause' });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPersistentDataSaved method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPersistentDataSaved((gameID: string, deviceID: string, key: string) => {
            postToWebView({
                action: 'onPersistentDataSaved',
                data: { gameID, deviceID, key },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPersistentDataRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPersistentDataRetrieve((gameID: string, deviceID: string, key: string, data: any) => {
            postToWebView({
                action: 'onPersistentDataRetrieve',
                data: { gameID, deviceID, key, data },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onHighscoreRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoreRetrieve(
            (gameID: string, deviceID: string, highscore: Highscore, level?: string) => {
                postToWebView({
                    action: 'onHighscoreRetrieve',
                    data: { gameID, deviceID, highscore, level },
                });
            }
        );
        return unsubscribe;
    }, []);

    /* Listen from onHighscoresRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoresRetrieve(
            (gameID: string, highscores: Array<Highscore>, group?: string | Array<string>, level?: string) => {
                postToWebView({
                    action: 'onHighscoresRetrieve',
                    data: { gameID, highscores, group, level },
                });
            }
        );
        return unsubscribe;
    }, []);

    /* Listen from onHighscoreSaved method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoreSaved((gameID: string, deviceID: string, level?: string) => {
            postToWebView({
                action: 'onHighscoreSaved',
                data: { gameID, deviceID, level },
            });
        });
        return unsubscribe;
    }, []);

    // Transmit child level messages without interfering
    // with the parent application
    useListener(TRANSMIT_CODE, postToWebView, []);

    // Receive messages from the underlying WebView (the child app)
    // and process it
    // -- ex : { }
    useEffect(() => {
        const webviewMessageHandler = (event: any) => {
            const { data } = event;
            if (!data || typeof data !== 'object') return;
            // Création IA (régie TV UnQuiz) : uniquement depuis l'iframe du jeu.
            const fromGame = event.source === webviewRef.current?.contentWindow;
            if (handleAiBridgeMessage(data, { fromGame, clientID, post: postToWebView })) return;
            if (data.action === 'onReady') {
                setWebviewLoaded(true);
                //
                const devices: any[] = [];
                for (const screenID of API.getScreenIDs()) {
                    devices.push({
                        deviceID: screenID,
                        data: {
                            player: API.getPlayer(screenID),
                            state: API.getDeviceState(screenID),
                        },
                    });
                }

                for (const gamepadID of API.getGamepadIDs()) {
                    devices.push({
                        deviceID: gamepadID,
                        data: {
                            player: API.getPlayer(gamepadID),
                            state: API.getDeviceState(gamepadID),
                        },
                    });
                }

                postToWebView({
                    action: 'ready',
                    data: {
                        hostID: API.getHostID(),
                        myDeviceID: API.getDeviceID(),
                        me, // Current user info
                        type, // 'screen' or 'gamepad'
                        devices,
                        session: {
                            id: API.getSessionID(),
                            url: API.getSessionLink(),
                        },
                        mute: API.isMute(),
                        language,
                        companyId: clientID, // Pass client ID to the game
                    },
                });
            } else if (data.action === 'send') {
                // console.log(
                //   'Game Runner Gamepad Send delay, ',
                //   Date.now() - data.data?.data?.data?.timestamp
                // );
                API.send(data.to, TRANSMIT_CODE, data.data);
            } else if (data.action === 'broadcast') {
                API.broadcast(TRANSMIT_CODE, data.data);
                // } else if (data.action === 'navigateToHome') {
                //     API.navigateToHome();
                // } else if (data.action === 'navigateTo') {
                //     API.navigateTo(data.data.url);
            } else if (data.action === 'savePersistentData') {
                API.savePersistentData(data.data.gameID, data.data.deviceID, data.data.key, data.data.data);
            } else if (data.action === 'retrievePersistentData') {
                API.retrievePersistentData(data.data.gameID, data.data.deviceID, data.data.key);
            } else if (data.action === 'saveHighscore') {
                API.saveHighscore(data.data.gameID, data.data.deviceID, data.data.score, data.data.level);
            } else if (data.action === 'saveQuiz') {
                if (clientID) {
                    const createdBy = user?.firstname || undefined;
                    const quizDataWithCreator = {
                        ...data.data.quizData,
                        createdBy,
                    };
                    Database.quizzes
                        .saveQuiz(clientID, data.data.gameID, data.data.quizId, quizDataWithCreator)
                        .then(() => {
                            postToWebView({
                                action: 'onQuizSaved',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    // Renvoyé tel quel : le jeu relie chaque réponse à SON envoi.
                                    requestId: data.data.requestId,
                                },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error saving quiz:', error);
                            Sentry.captureException(error, { tags: { area: 'quiz_bridge' } });
                            postToWebView({
                                action: 'onQuizSaveError',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    requestId: data.data.requestId,
                                    error: error?.message,
                                },
                            });
                        });
                } else {
                    // Défensif (aujourd'hui l'écran passe toujours un clientID) : sans
                    // établissement, répondre quand même — un pont muet laisse le jeu
                    // bloqué sur « Enregistrement… » (perte de 2 h de travail, 15/10).
                    Sentry.captureMessage('saveQuiz sans clientID', {
                        level: 'warning',
                        tags: { area: 'quiz_bridge' },
                    });
                    postToWebView({
                        action: 'onQuizSaveError',
                        data: {
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            requestId: data.data.requestId,
                            error: 'no-client',
                        },
                    });
                }
            } else if (data.action === 'retrieveQuiz') {
                if (clientID) {
                    Database.quizzes
                        .retrieveQuiz(clientID, data.data.gameID, data.data.quizId)
                        .then((quizData) => {
                            postToWebView({
                                action: 'onQuizRetrieve',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, quizData },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error retrieving quiz:', error);
                        });
                }
            } else if (data.action === 'retrieveQuizzes') {
                if (clientID) {
                    Database.quizzes
                        .retrieveQuizzes(clientID, data.data.gameID, data.data.total)
                        .then((quizzes) => {
                            postToWebView({
                                action: 'onQuizzesRetrieve',
                                data: { gameID: data.data.gameID, quizzes },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error retrieving quizzes:', error);
                        });
                }
            } else if (data.action === 'deleteQuiz') {
                if (clientID) {
                    Database.quizzes
                        .deleteQuiz(clientID, data.data.gameID, data.data.quizId)
                        .then(() => {
                            postToWebView({
                                action: 'onQuizDeleted',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error deleting quiz:', error);
                            postToWebView({
                                action: 'onQuizDeleteError',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, error: error.message },
                            });
                        });
                }
            } else if (data.action === 'uploadQuizCoverImage') {
                if (clientID) {
                    Database.quizzes
                        .uploadCoverImageFromDataUrl({
                            clientID,
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            fileName: data.data.fileName,
                            dataUrl: data.data.dataUrl,
                        })
                        .then(({ url, path }) => {
                            postToWebView({
                                action: 'onQuizCoverImageUploaded',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, url, path },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error uploading cover image:', error);
                            postToWebView({
                                action: 'onQuizCoverImageUploadError',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, error: error.message },
                            });
                        });
                }
            } else if (data.action === 'uploadQuizImage') {
                // Upload générique (couverture OU image de question) : `slot` est
                // renvoyé tel quel pour corréler la réponse à la bonne cible.
                if (clientID) {
                    Database.quizzes
                        .uploadImageFromDataUrl({
                            clientID,
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            folder: data.data.folder,
                            fileName: data.data.fileName,
                            dataUrl: data.data.dataUrl,
                        })
                        .then(({ url, path }) => {
                            postToWebView({
                                action: 'onQuizImageUploaded',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    slot: data.data.slot,
                                    url,
                                    path,
                                },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error uploading quiz image:', error);
                            postToWebView({
                                action: 'onQuizImageUploadError',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    slot: data.data.slot,
                                    error: error.message,
                                },
                            });
                        });
                } else {
                    // Pas de clientID (ex. gamepad runner) : répondre l'échec plutôt
                    // que d'avaler la requête — le jeu attend une réponse pour
                    // débloquer son UI d'upload.
                    postToWebView({
                        action: 'onQuizImageUploadError',
                        data: {
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            slot: data.data.slot,
                            error: 'no clientID',
                        },
                    });
                }
            } else if (data.action === 'retrieveHighscore') {
                API.retrieveHighscore(data.data.gameID, data.data.deviceID, data.data.level);
            } else if (data.action === 'retrieveHighscores') {
                API.retrieveHighscores(data.data.gameID, data.data.total, data.data.group, data.data.level);
            } else if (data.action === 'debug') {
                console.log('[Debug] Debug message:', data.data);
            }
        };

        //
        window.addEventListener('message', webviewMessageHandler);

        return () => window.removeEventListener('message', webviewMessageHandler);
    }, []);

    return { isReady: webviewLoaded };
};
